import { Ref } from 'vue';
import { i18n } from '@libs/i18n';
import {
  OptionContractsChainRequest,
  UnderlyingEndDatesRequest,
  CashSettlementHistoryRequest,
  PhysicalSettlementHistoryRequest,
  PositionHistoryRequest,
  OptionFrozenHistoryParams,
} from '@libs/api-reference';
import {
  NegarCoveredCallIcon,
  NegarBullCallIcon,
  NegarBearPutIcon,
} from '@negar/icons';

const { t } = i18n.global;

const POSITIONS_SIDE = { buy: 1, sell: 2 } as const;

const POSITIONS_SIDE_LABEL = {
  buy: t('common.buy'),
  sell: t('common.sell'),
};

const CONTRACT_CHAIN_SIDE = {
  0: t('option.contractType.both'),
  1: t('option.contractType.buy'),
  2: t('option.contractType.sell'),
};

const GUARANTEE_METHOD = {
  1: t('option.cashAmount'),
  2: t('option.baseAsset'),
  3: t('misc.position'),
};

const GUARANTEE_COUNTER = {
  1: t('misc.rial'),
  2: t('modules.order.stock'),
};

const STRATEGY_TYPE = {
  1: {
    label: t('LongButterflyCallSpread'),
    icon: '',
  },
  2: {
    label: t('LongButterflyPutSpread'),
    icon: '',
  },
  3: {
    label: t('option.strategyType.bullCall'),
    icon: NegarBullCallIcon,
  },
  4: {
    label: t('option.strategyType.bearPut'),
    icon: NegarBearPutIcon,
  },
  5: {
    label: t('option.strategyType.coveredCall'),
    icon: NegarCoveredCallIcon,
  },
  6: {
    label: t('ShortButterflyCallSpread'),
    icon: '',
  },
  7: {
    label: t('ShortButterflyPutSpread'),
    icon: '',
  },
  8: {
    label: t('BearCallSpread'),
    icon: '',
  },
  9: {
    label: t('BullPutSpread'),
    icon: '',
  },
  10: {
    label: t('ShortStraddle'),
    icon: '',
  },
  11: {
    label: t('ShortStrangle'),
    icon: '',
  },
  12: {
    label: t('NakedShortCall'),
    icon: '',
  },
  13: {
    label: t('NakedShortPut'),
    icon: '',
  },
};

const OPTION_QUERY_KEYS = {
  optionContract: (isin: Ref<string>) => ['option-contract', isin],
  contractsChain: (params: Ref<OptionContractsChainRequest>) => [
    'contracts-chain',
    params,
  ],
  strategies: ['option-strategies'],
  strategyLegs: (isin: string) => ['strategy-legs', isin],
  underlyingAssetIsins: ['underlying-asset-isins'],
  underlyingAssetIsinEndDates: (params: Ref<UnderlyingEndDatesRequest>) => [
    'underlying-asset-isins',
    params,
  ],
  positions: ['option-positions'],
  positionHistory: (arg: Ref<PositionHistoryRequest>) => [
    'position-history',
    arg,
  ],
  cashSettlementHistory: (arg: Ref<CashSettlementHistoryRequest>) => [
    'cash-settlement-history',
    arg,
  ],
  physicalSettlementHistory: (arg: Ref<PhysicalSettlementHistoryRequest>) => [
    'physical-settlement-history',
    arg,
  ],
  frozenHistory: (arg: Ref<OptionFrozenHistoryParams>) => [
    'frozen-history',
    arg,
  ],
};

const OPTION_HISTORY_SIDE = {
  positionsHistory: 0,
  cashSettlementHistory: 1,
  physicalSettlementHistory: 2,
} as const;

const DAY_PER_MILLISECONDS = 60 * 1000 * 60 * 24;

const CONTRACT_INFO_CHANGED_CHANNEL = 'options-contracts-';
const POSITION_CHANNEL_PREFIX = 'options-users-';
const SETTLEMENT_CHANNEL = 'options-settlements';
const FROZEN_SYMBOLS_HISTORY_STATE = {
  requested: 1,
  done: 8,
  canceled: 16,
  rejected: 4,
  approve: 2,
};

const FREEZE_UNFREEZE_PARAMS = {
  requestGatewayId: 3,
  executorBrokerBranchId: 166,
};

const SETTLEMENT_TYPE = {
  0: i18n.global.t('option.notSettlemented'),
  1: i18n.global.t('option.settlementRequest.cash'),
  2: i18n.global.t('option.settlementRequest.physical'),
  3: `${i18n.global.t('option.settlementRequest.cash')} - ${i18n.global.t(
    'option.settlementRequest.physical'
  )}`,
};

const OPTION_REQUEST_STATE = {
  1: i18n.global.t('option.waitingForApply'),
  2: i18n.global.t('misc.notApplied'),
  3: i18n.global.t('option.waitingForApply'),
  4: i18n.global.t('option.waitingForApply'),
  5: i18n.global.t('misc.applied'),
  6: i18n.global.t('misc.notApplied'),
};

const STRATEGY_LEGS_TYPE = {
  0: null,
  1: t('option.cashAmount'),
  2: t('option.baseAsset'),
  3: t('option.position'),
  4: t('option.combined'),
};

export {
  OPTION_QUERY_KEYS,
  POSITIONS_SIDE,
  CONTRACT_INFO_CHANGED_CHANNEL,
  POSITION_CHANNEL_PREFIX,
  SETTLEMENT_CHANNEL,
  POSITIONS_SIDE_LABEL,
  CONTRACT_CHAIN_SIDE,
  GUARANTEE_METHOD,
  GUARANTEE_COUNTER,
  STRATEGY_TYPE,
  STRATEGY_LEGS_TYPE,
  OPTION_HISTORY_SIDE,
  DAY_PER_MILLISECONDS,
  FROZEN_SYMBOLS_HISTORY_STATE,
  FREEZE_UNFREEZE_PARAMS,
  SETTLEMENT_TYPE,
  OPTION_REQUEST_STATE,
};
